/** Projects — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "@/components/ui/icon";
import type { Project } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { StatusBadge } from "@/components/app/StatusBadge";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "./_shared";

export function ProjectList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteProject } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Project | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Project, unknown>[]>(
    () => [
      { id: "name", accessorKey: "name", header: "Name" },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="line-clamp-1 max-w-xs text-muted-foreground">
            {row.original.description ?? "—"}
          </span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            project={row.original}
            onEdit={() => go({ to: editUrl("projects", row.original.id) })}
            onShow={() => go({ to: showUrl("projects", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Project, HttpError>({
    columns,
    refineCoreProps: {
      resource: "projects",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        description="Construction and development projects."
        action={
          <Button onClick={() => go({ to: createUrl("projects") })}>
            <Plus className="mr-2 h-4 w-4" /> New Project
          </Button>
        }
      />

      <SearchBox value={search} onChange={setSearch} placeholder="Search projects…" />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No projects yet. Create your first project."
        onRowClick={(p) => go({ to: showUrl("projects", p.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete project?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium">{pendingDelete?.name}</span>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteProject({ resource: "projects", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  project: Project;
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ProjectList;
